import { CompanionPanel } from "@components/CompanionPanel";
import { PimsActivityCard } from "@pims/PimsActivityCard";
import {
  hasHistoryFilters,
  historyState,
  inFlightState,
  isCancellable,
  isPartialWriteRisk,
  isRetryable,
  recordKey,
  recordLabel,
  type ActivityState,
  type PimsActivityItem,
  type PimsHistoryFilters as HistoryFilters,
  type PimsRecordRef,
} from "@pims/pimsActivityClient";
import {
  useCancelPimsTask,
  usePimsActivity,
  usePimsHistory,
  useRetryPimsTask,
} from "@pims/pimsActivityQueries";
import { PimsAudit } from "@pims/PimsAudit";
import { PimsHistoryFilters } from "@pims/PimsHistoryFilters";
import {
  ConfirmDialog,
  EmptyNote,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { formatTime } from "@utils/format-date";
import { useEffect, useState, type ReactNode } from "react";

export const PimsActivityPanel = () => {
  const [filters, setFilters] = useState<HistoryFilters>({});
  // The record-selector's option list (PIMS session replay POC). Lifted above
  // the suspending `CompanionPanel` boundary and MERGED (never replaced) as
  // each history page resolves: a record filter narrows the page to just its
  // own rows, so replacing the list with `uniqueRecords(history)` would (a)
  // collapse the dropdown to only the selected record — no way to switch
  // straight to a different one without clearing first — and (b) blank the
  // selected option's label to the placeholder the moment a filtered page
  // comes back empty (DSDropdown resolves the label via `options.find(value)`).
  // Accumulating by `recordKey` across every page seen this session keeps
  // every previously-surfaced record selectable and its label always
  // resolvable, in first-seen order.
  const [records, setRecords] = useState<PimsRecordRef[]>([]);
  // The history row whose replay fills the detail pane. Master-detail: the
  // list on the left drives what the wide right pane shows (WEB-3237). Lives
  // here, above the suspending list, so a filter change that re-fetches the
  // page doesn't reset the open replay.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The write awaiting retry confirmation. Lifted with the mutation for the same
  // reason as `selectedId`, plus one of its own: React only HIDES a suspended
  // subtree, so a modal below the boundary would strand its body scroll-lock
  // while showing nothing.
  const [confirming, setConfirming] = useState<PimsActivityItem | null>(null);
  const retry = useRetryPimsTask();

  const onRetry = (item: PimsActivityItem) =>
    isPartialWriteRisk(item) ? setConfirming(item) : retry.mutate(item.id);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 font-body">
      <div className="px-2 pt-2">
        <PimsHistoryFilters
          filters={filters}
          onChange={setFilters}
          records={records}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 md:flex-row">
        <div className="flex min-h-0 flex-col overflow-y-auto md:w-80 md:shrink-0">
          <CompanionPanel
            skeleton={<PimsActivitySkeleton />}
            fallback={(retry) => (
              <PanelError label="PIMS activity" onRetry={retry} />
            )}
          >
            <PimsActivityContent
              filters={filters}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onRecords={(page) =>
                setRecords((prev) => mergeRecords(prev, page))
              }
              onRetry={onRetry}
              retryingId={retry.isPending ? (retry.variables ?? null) : null}
            />
          </CompanionPanel>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-paper border border-rule bg-card shadow-lift">
          <PimsAuditPane taskId={selectedId} />
        </div>
      </div>
      <RetryConfirm
        item={confirming}
        onClose={() => setConfirming(null)}
        onConfirm={(item) => {
          retry.mutate(item.id);
          setConfirming(null);
        }}
      />
    </div>
  );
};

/**
 * Confirms a write retry, naming the write it is about (WEB-3270) — the row sits
 * behind the backdrop and the list often holds several failures, so the goal and
 * record are the only way to check you're re-running the right one.
 */
const RetryConfirm = ({
  item,
  onClose,
  onConfirm,
}: {
  item: PimsActivityItem | null;
  onClose: () => void;
  onConfirm: (item: PimsActivityItem) => void;
}) => (
  <ConfirmDialog
    open={item !== null}
    onCancel={onClose}
    onConfirm={() => item && onConfirm(item)}
    title="Re-run this PIMS write?"
    confirmLabel="Re-run"
    cancelLabel="Cancel"
    confirmTestId="pims-retry-confirm-accept"
    cancelTestId="pims-retry-confirm-cancel"
  >
    {item && (
      <div className="flex flex-col gap-3" data-testid="pims-retry-confirm">
        <p
          className="whitespace-pre-line text-sm font-medium text-ink"
          data-testid="pims-retry-confirm-goal"
        >
          {item.goal}
        </p>
        {item.record && (
          <p className="text-xs text-annotation">{recordLabel(item.record)}</p>
        )}
        <p className="text-sm text-ink-body">
          This write didn’t finish, so part of it may already be in the PIMS.
          Re-running repeats the whole instruction, which can duplicate whatever
          landed the first time. Check the record before continuing.
        </p>
      </div>
    )}
  </ConfirmDialog>
);

/** The detail pane. Empty until a history row is selected; then it mounts the
 * selected write's replay, keyed by task id so switching selection resets the
 * audit's presigned-URL refresh guard rather than carrying it across writes. */
const PimsAuditPane = ({ taskId }: { taskId: string | null }) => {
  if (!taskId)
    return (
      <EmptyNote data-testid="pims-audit-empty-pane">
        Pick a write and I&rsquo;ll show you what I did.
      </EmptyNote>
    );
  return (
    <div className="p-3">
      <PimsAudit key={taskId} taskId={taskId} />
    </div>
  );
};

const PimsActivityContent = ({
  filters,
  selectedId,
  onSelect,
  onRecords,
  onRetry,
  retryingId,
}: {
  filters: HistoryFilters;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRecords: (records: PimsRecordRef[]) => void;
  onRetry: (item: PimsActivityItem) => void;
  retryingId: string | null;
}) => {
  const { data } = usePimsActivity();
  const { data: history } = usePimsHistory(filters);
  const cancel = useCancelPimsTask();
  const showQueued = data.queued.length > 0;
  const filtering = hasHistoryFilters(filters);

  // Merge this page's unique non-null records into the caller's accumulated
  // set (see the `records` state comment in `PimsActivityPanel`) rather than
  // replacing it, so the record selector's options only ever grow.
  useEffect(() => {
    onRecords(uniqueRecords(history));
  }, [history, onRecords]);

  const cancelProps = (item: PimsActivityItem, state: ActivityState) =>
    isCancellable(item, state)
      ? {
          onCancel: () => cancel.mutate(item.id),
          cancelling: cancel.isPending && cancel.variables === item.id,
        }
      : {};

  // `retriedBy` is what retires the button: the source keeps its `failed` status
  // forever, so it would otherwise stay live and invite a duplicate PIMS write.
  // Truthiness, not `=== null` — an older response leaves it `undefined`, and the
  // safe reading of "I can't tell" is to keep offering the button (the backend
  // still caps it at one retry) rather than to hide a working control.
  const retryProps = (item: PimsActivityItem) =>
    isRetryable(item) && !item.retriedBy
      ? {
          onRetry: () => onRetry(item),
          // Per-item: one mutation backs every row and `variables` holds only the
          // latest call, so retrying a second row would re-enable the first.
          retrying: retryingId === item.id,
        }
      : {};

  const groups = groupByRecord(history);

  return (
    <div className="flex flex-col gap-4">
      {data.inFlight.length > 0 && (
        <Section title="In flight">
          {data.inFlight.map((item) => {
            const state = inFlightState(item);
            return (
              <PimsActivityCard
                key={item.id}
                item={item}
                state={state}
                {...cancelProps(item, state)}
              />
            );
          })}
        </Section>
      )}

      {showQueued && (
        <Section
          title="Queued"
          caption={flushCaption(data.queued.length, data.nextFlushAt)}
        >
          {data.queued.map((item) => (
            <PimsActivityCard
              key={item.id}
              item={item}
              state="queued"
              {...cancelProps(item, "queued")}
            />
          ))}
        </Section>
      )}

      {data.staff.length > 0 && (
        <Section title="Waiting on staff">
          {data.staff.map((item) => (
            <PimsActivityCard
              key={item.id}
              item={item}
              state="awaiting_staff"
            />
          ))}
        </Section>
      )}

      <Section title="History">
        {history.length > 0 ? (
          <div className="flex flex-col gap-4">
            {groups.map((group) => (
              <div key={group.key} className="flex flex-col gap-2">
                <h3
                  className="text-annotation-caps text-annotation"
                  data-testid={`pims-history-group-${group.key}`}
                >
                  {group.label}
                </h3>
                <div className="flex flex-col gap-2">
                  {group.items.map((item) => (
                    <PimsActivityCard
                      key={item.id}
                      item={item}
                      state={historyState(item)}
                      onSelect={() => onSelect(item.id)}
                      selected={selectedId === item.id}
                      {...retryProps(item)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="p-4 text-center text-sm text-annotation">
            {filtering
              ? "Nothing settled matches these filters."
              : "I haven’t settled any PIMS work yet."}
          </p>
        )}
      </Section>
    </div>
  );
};

/** The record grouping key for the "no record" bucket — never collides with a
 * real `${kind}:${uuid}` key (both are always defined, non-empty strings). */
const OTHER_GROUP_KEY = "none";

interface HistoryGroup {
  key: string;
  label: string;
  items: PimsActivityItem[];
}

/**
 * Groups settled history items chronologically beneath their primary record, keyed by
 * `${kind}:${uuid}` so a patient and a client can't collide on a shared uuid. A task
 * touching several subjects still appears once, under its single immutable `record` —
 * no per-subject fan-out. Recordless items collect under "Other PIMS activity".
 *
 * Group order follows first appearance in the already-newest-first list, so the group
 * holding the most recent write leads; "Other PIMS activity" is always appended last,
 * keeping it a stable trailing bucket rather than jumping around.
 */
const groupByRecord = (items: PimsActivityItem[]): HistoryGroup[] => {
  const byKey = new Map<string, HistoryGroup>();
  for (const item of items) {
    const key = item.record ? recordKey(item.record) : OTHER_GROUP_KEY;
    const label = item.record
      ? recordLabel(item.record)
      : "Other PIMS activity";
    const group = byKey.get(key);
    if (group) group.items.push(item);
    else byKey.set(key, { key, label, items: [item] });
  }
  const groups = [...byKey.values()];
  const other = groups.filter((g) => g.key === OTHER_GROUP_KEY);
  const withRecord = groups.filter((g) => g.key !== OTHER_GROUP_KEY);
  return [...withRecord, ...other];
};

/** The unique non-null records referenced by `items`, first-seen order. */
const uniqueRecords = (items: PimsActivityItem[]): PimsRecordRef[] => {
  const seen = new Set<string>();
  const records: PimsRecordRef[] = [];
  for (const item of items) {
    if (!item.record) continue;
    const key = recordKey(item.record);
    if (seen.has(key)) continue;
    seen.add(key);
    records.push(item.record);
  }
  return records;
};

/** Accumulates `next` into `prev` by `recordKey`, keeping `prev`'s ordering
 * and appending only genuinely new records — the record selector's options
 * only ever grow across pages/filters, never shrink (see the `records` state
 * comment above). */
const mergeRecords = (
  prev: PimsRecordRef[],
  next: PimsRecordRef[],
): PimsRecordRef[] => {
  const seen = new Set(prev.map(recordKey));
  const additions = next.filter((r) => !seen.has(recordKey(r)));
  return additions.length > 0 ? [...prev, ...additions] : prev;
};

const flushCaption = (count: number, nextFlushAt: string | null): string => {
  const items = `${count} ${count === 1 ? "item goes" : "items go"} in`;
  return nextFlushAt
    ? `${items} at the ${formatTime(nextFlushAt)} flush`
    : `${items} at the next flush`;
};

const Section = ({
  title,
  caption,
  children,
}: {
  title: string;
  caption?: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-2">
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="font-display text-sm font-medium text-ink">{title}</h2>
      {caption && <span className="text-xs text-annotation">{caption}</span>}
    </div>
    {children}
  </section>
);

const PimsActivitySkeleton = () => (
  <SkeletonStack count={4} className="gap-3" itemClassName="h-16" />
);
